import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
// mantine
import { ScrollArea, Switch, TextInput } from '@mantine/core';
import { useDebouncedState, useDisclosure } from '@mantine/hooks';
// icons
import { Search } from 'lucide-react';
// components
import type { TAttribute, TAttributeData, TPersona } from '@newo/api/gateway';
import PersonasAutocomplete from '@/modules/Personas/components/PersonasAutocomplete';
import AttributeForm from '@/pages/Attributes/components/AttributeForm';
import Drawer from '@/components/Drawer';
import NoRecords from '@/components/NoRecords';
import GroupNavigation from '@/pages/Attributes/components/GroupNavigation';
import Navigation from '@/pages/Attributes/components/Navigation';
import GroupTitle from '@/pages/Attributes/components/GroupTitle';
import Attribute from '@/pages/Attributes/components/Attribute';
import ContextMenu from '@/pages/Attributes/Personas/components/ContextMenu';
// utils
import {
    useCreatePersonaAttributeMutation,
    useDeletePersonaAttributeMutation,
    useUpdatePersonaAttributeMutation
} from '@/services/customer/personas/personas.queries';
import {
    usePersonaAttributesQuery,
    usePersonaQuery
} from '@/services/bff/bff.queries.ts';
import { PATHS } from '@/const';
import { openConfirmDeleteModal } from '@/utils/ui.tsx';
import { PageContainer } from '@/components/PageContainer/PageContainer.tsx';
import { sortByNumericPrefix, sortByNumericPrefixProperty } from '@/utils';
import { useAppStore } from '@/store/app';

const DEBOUNCE_TIMEOUT = 200;

const Personas = () => {
    const navigate = useNavigate();
    const { group: groupParameter, personaId } = useParams();
    const group = groupParameter
        ? decodeURIComponent(groupParameter)
        : undefined;

    const store = useAppStore();
    const [selectedAttribute, setSelectedAttribute] = useState<
        TAttribute | undefined
    >();
    const [isOpened, handlers] = useDisclosure();
    const [searchTerm, setSearchTerm] = useDebouncedState('', DEBOUNCE_TIMEOUT);

    const { data: persona } = usePersonaQuery(personaId);
    const { data, refetch } = usePersonaAttributesQuery({
        personaId,
        params: {
            query: searchTerm,
            include_hidden: store.isAttrsHiddenShown
        }
    });
    const { mutateAsync: createAttribute } =
        useCreatePersonaAttributeMutation();
    const { mutate: deleteAttribute } = useDeletePersonaAttributeMutation();
    const { mutateAsync: updateAttribute } =
        useUpdatePersonaAttributeMutation();

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.currentTarget.value);
        navigate(`${PATHS.attributes.root}/personas/${personaId ?? ''}`);
    };

    const handleOpen = () => {
        handlers.open();
    };

    const handleClose = () => {
        handlers.close();
        setSelectedAttribute(undefined);
    };

    const handlePersonaSelect = (p?: TPersona) => {
        navigate(`${PATHS.attributes.root}/personas/${p?.id ?? ''}`);
    };

    const handleAttributeSubmit = async ({
        attributeId,
        data: formData
    }: {
        attributeId?: string;
        data: TAttributeData;
    }) => {
        if (!personaId) {
            return;
        }
        await (attributeId
            ? updateAttribute({ personaId, attributeId, data: formData })
            : createAttribute({ personaId, data: formData }));
        handleClose();
        refetch();
    };

    const handleEdit = (attribute: TAttribute) => {
        handleOpen();
        setSelectedAttribute(attribute);
    };

    const handleDelete = (attribute: TAttribute) => {
        openConfirmDeleteModal({
            title: `Delete ${attribute.idn} attribute?`,
            children: `Are you sure you want to delete ${attribute.idn} attribute?`,
            confirmLabel: 'Delete Attribute',
            onConfirm: () => {
                if (!personaId) {
                    return;
                }
                deleteAttribute(
                    { attributeId: attribute.id, personaId },
                    {
                        onSuccess: () => {
                            refetch();
                        }
                    }
                );
            }
        });
    };

    const groups = sortByNumericPrefix(data?.groups ?? []);

    const items = useMemo(() => {
        const attributes = data?.attributes ?? [];
        let filteredAttributes: TAttribute[];

        if (group === 'Unassigned') {
            filteredAttributes = attributes.filter(
                (attribute) =>
                    attribute.group === 'unassigned' || !attribute.group
            );
        } else if (group) {
            filteredAttributes = attributes.filter(
                (attribute) => attribute.group === group
            );
        } else {
            filteredAttributes = attributes;
        }

        return sortByNumericPrefixProperty(filteredAttributes, 'title');
    }, [data?.attributes, group]);

    // TODO: Move unassigned to BE
    const unassignedItems = data?.attributes.filter(
        (attribute) => attribute.group === 'unassigned' || !attribute.group
    );

    return (
        <div className="w-full h-full">
            <PageContainer className="pt-6 !grid !gap-4 !overflow-hidden !grid-cols-[300px_1fr]">
                <div className="flex flex-col gap-4 overflow-hidden">
                    <Navigation className="mb-1" value="personas" />
                    <PersonasAutocomplete
                        label="Select persona"
                        personaName={persona?.name}
                        placeholder="Start typing..."
                        onSelect={handlePersonaSelect}
                    />
                    <ScrollArea h="100%" scrollbars="y" type="hover">
                        {persona ? (
                            <GroupNavigation
                                withAll
                                baseUrl={`${PATHS.attributes.root}/personas/${persona.id}`}
                                groups={groups}
                                selectedGroup={group}
                                withUnassigned={!!unassignedItems?.length}
                            />
                        ) : undefined}
                    </ScrollArea>
                </div>
                <div className="overflow-hidden pb-20">
                    <div className="flex items-center gap-8  pb-4">
                        <TextInput
                            className="w-1/2"
                            defaultValue={searchTerm}
                            leftSection={<Search />}
                            placeholder="Search by idn, title, group"
                            size="sm"
                            onChange={handleSearchChange}
                        />
                        <Switch
                            checked={store.isAttrsHiddenShown}
                            className="ml-auto"
                            label="Show Hidden"
                            onChange={() => {
                                store.toggleAttrsHiddenShown();
                            }}
                        />

                        <ContextMenu
                            isDisabled={!personaId}
                            onAdd={handleOpen}
                        />
                    </div>

                    <ScrollArea h="100%" scrollbars="y" type="hover">
                        <div className="min-h-0 gap-4 pr-4">
                            <GroupTitle>{group ?? 'all'}</GroupTitle>
                            {items.length === 0 ? <NoRecords /> : undefined}
                            {items.map((item) => (
                                <Attribute
                                    attribute={item}
                                    key={item.id}
                                    onDelete={handleDelete}
                                    onEdit={handleEdit}
                                    onSave={handleAttributeSubmit}
                                />
                            ))}
                        </div>
                    </ScrollArea>
                </div>
            </PageContainer>
            <Drawer
                withOverlay
                name="customer-attributes"
                opened={isOpened}
                position="right"
                size="md"
                onClose={handleClose}
            >
                <AttributeForm
                    attribute={selectedAttribute}
                    groups={groups}
                    onClose={handleClose}
                    onSubmit={handleAttributeSubmit}
                />
            </Drawer>
        </div>
    );
};

export default Personas;
